<template>
	<section>
		<ButtonBack />
		<h1>Statistiken</h1>
		<h2>Inhalte für Bericht definieren</h2>

		<FaelleSearchForm
			@doSearch="doSearch"
			:id="formId"
			ref="faelleSearchForm"
			:requestInitial="{ isPublished: { label: 'Ja', value: true } }"
			:forStatistics="true"
		/>
	</section>

	<div class="bg--white flex flex-grow">
		<section id="searchresults" :class="{ 'searchresults--disabled': casesTableLoading }">
			<h2>{{ $getCountErgebnisseLabel(casesTotalCount, 'Fälle') }} gefunden</h2>
			<p v-if="loadedCasesCount < casesTotalCount">{{ loadedCasesCount }} von {{ casesTotalCount }} Fällen geladen...</p>

			<TheButton type="danger" @buttonclick="openReport()" :disabled="filteredCases.length === 0">Bericht erstellen</TheButton>
		</section>
	</div>

	<div class="bg--white flex flex-grow" v-if="!casesTableLoading">
		<section id="searchresults">
			<el-row class="statistik__totalcounts">
				<TheBox :title="`${caseComplaintsTotalCount}`">
					<span>Beschwerden gesamt</span>
				</TheBox>
				<TheBox :title="`${filteredCases.length}`">
					<span>Entscheidungen gesamt</span>
				</TheBox>
			</el-row>

			<CategoryBarChart :categoryData="decisionCategories" :totalCount="filteredCases.length" :totalCountType="'Entscheidungen'" />

			<h3>Angegebener Grund</h3>
			<TheTable :tableData="complaintCategories" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Grund der Beschwerde" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>

			<h3>Medium</h3>
			<TheTable :tableData="decisionMediums" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Betroffenes Medium" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>

			<h3>Branche</h3>
			<TheTable :tableData="casesSectors" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Betroffene Branche" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>

			<h3>Reichweite</h3>
			<TheTable :tableData="caseReaches" :showMaxRows="100" @rowClick="null" class="table--withsumrow">
				<el-table-column prop="name" label="Betroffene Reichweite" />
				<el-table-column prop="count" label="Absolut (Anzahl)" width="160" />
				<el-table-column prop="percent" label="Relativ (Prozent)" width="160" :class-name="'cell--alignleft'" />
			</TheTable>
		</section>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import TheBox from '@/components/TheBox.vue';
import TheButton from '@/components/TheButton.vue';
import TheForm from '@/components/TheForm.vue';
import TheTable from '@/components/TheTable.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import CategoryBarChart from '@/components/charts/CategoryBarChart.vue';
import FaelleSearchForm from '@/components/forms/FaelleSearchForm.vue';
import { searchCases } from '@/requests/searches.js';
import { CaseResponse, CaseState, ComplaintResponse, SearchCaseRequest } from '@/types/types';
import { validateForm } from '@/utils/functions';
import { prepareSearchRequest } from '@/utils/search/searchUtils';
import { FormInstance } from 'element-plus';
import { reaches } from '../utils/reaches';

interface SearchCasesData {
	filteredCases: CaseResponse[];
	casesTotalCount: number;
	loadedCasesCount: number;
	caseComplaints: ComplaintResponse[];
	caseComplaintsTotalCount: number;
	casesTableLoading: boolean;
	formId: string;
	itemsPerPage: number;
}

export default defineComponent({
	name: 'StatistikView',
	components: {
		ButtonBack,
		TheForm,
		FaelleSearchForm,
		TheButton,
		TheBox,
		TheTable,
		CategoryBarChart,
	},
	data(): SearchCasesData {
		return {
			filteredCases: [],
			casesTotalCount: 0,
			loadedCasesCount: 0,
			casesTableLoading: false,
			caseComplaints: [],
			caseComplaintsTotalCount: 0,
			formId: 'statistikSearchForm',
			itemsPerPage: 14,
		};
	},
	computed: {
		searchFormRef() {
			return this.$refs.faelleSearchForm as InstanceType<typeof FaelleSearchForm>;
		},
		complaintCategories() {
			if (this.caseComplaints.length === 0) return [];

			let categoryCounts = this.caseComplaints
				.reduce(
					(acc, item) => {
						const categoryName = `${item.category?.number} ${item.category?.name}`;
						const existingCategory = acc.find((c) => c.name === categoryName);

						if (existingCategory) {
							existingCategory.count++;
						} else {
							acc.push({
								name: categoryName,
								count: 1,
								percent: '',
							});
						}

						return acc;
					},
					[] as Array<{ name: string; count: number; percent: string }>,
				)
				.map((category) => {
					let percent = Number(((category.count / this.caseComplaints.length) * 100).toFixed(2));
					return {
						...category,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			categoryCounts.push({ name: 'Summe', count: categoryCounts.reduce((acc, cat) => acc + cat.count, 0), percent: '100%' });

			return categoryCounts;
		},
		decisionCategories() {
			if (this.filteredCases.length === 0) return [];

			return this.filteredCases.reduce(
				(acc, item) => {
					let name = '';
					if (item.state) {
						name = this.$getStateFullLabel('case', item.state, false);
					}

					const allCaseStates = [
						CaseState.DecisionNoReason,
						CaseState.DecisionSensitization,
						CaseState.DecisionStop,
						CaseState.NotResponsible,
						CaseState.ProcedureNotPossible,
						CaseState.ImmediateSubjectWithdrawal,
						CaseState.Unfounded,
					];
					const allCaseStatesLabels = allCaseStates.map((state) => this.$getStateFullLabel('case', state, false));

					// check if the state already exists
					const index = acc.findIndex((category) => category.category.name === name);

					if (index > -1) {
						acc[index].count++;
					} else {
						acc.push({
							category: { number: '', name: name },
							count: 1,
						});
					}

					allCaseStatesLabels.forEach((state) => {
						const index = acc.findIndex((category) => category.category.name === state);
						if (index === -1) {
							acc.push({
								category: { number: '', name: state },
								count: 0,
							});
						}
					});

					return acc;
				},
				[] as Array<{ category: { number: string; name: string }; count: number }>,
			);
		},
		decisionMediums() {
			if (this.filteredCases.length === 0) return [];

			const mediumCounts = this.filteredCases
				.reduce(
					(acc, item) => {
						item.mediums.forEach((med) => {
							const mediumName = med.name;
							const existingMedium = acc.find((m) => m.name === mediumName);

							if (existingMedium) {
								existingMedium.count++;
							} else {
								acc.push({
									name: mediumName,
									count: 1,
									percent: '',
								});
							}
						});

						return acc;
					},
					[] as Array<{ name: string; count: number; percent: string }>,
				)
				.map((med) => {
					let percent = Number(((med.count / this.filteredCases.length) * 100).toFixed(2));

					return {
						...med,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			mediumCounts.push({ name: 'Summe', count: mediumCounts.reduce((acc, med) => acc + med.count, 0), percent: '100%' });

			return mediumCounts;
		},
		casesSectors() {
			if (this.filteredCases.length === 0) return [];

			let sectorCounts = this.filteredCases.reduce(
				(acc, item) => {
					item.sectors.forEach((sec) => {
						const secName = sec.name;
						const existingSector = acc.find((s) => s.name === secName);

						if (existingSector) {
							existingSector.count++;
						} else {
							acc.push({
								name: secName,
								count: 1,
								percent: '',
							});
						}
					});
					return acc;
				},
				[] as Array<{ name: string; count: number; percent: string }>,
			);

			const sectorCountsLength = sectorCounts.reduce((acc, cat) => acc + cat.count, 0);
			sectorCounts = sectorCounts
				.map((sec) => {
					let percent = Number(((sec.count / sectorCountsLength) * 100).toFixed(2));
					return {
						...sec,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			sectorCounts.push({ name: 'Summe', count: sectorCountsLength, percent: '100%' });

			return sectorCounts;
		},
		caseReaches() {
			if (this.filteredCases.length === 0) return [];

			let reachCounts = this.filteredCases.reduce(
				(acc, item) => {
					const allCaseReaches = reaches.map((reach) => reach.name!);

					item.reaches.forEach((reach) => {
						const reachName = reach.name;
						const existingReach = acc.find((r) => r.name === reachName);

						if (existingReach) {
							existingReach.count++;
						} else {
							acc.push({
								name: reachName,
								count: 1,
								percent: '',
							});
						}
					});

					allCaseReaches.forEach((reach) => {
						const existingReach = acc.find((r) => r.name === reach);
						if (!existingReach) {
							acc.push({
								name: reach,
								count: 0,
								percent: '',
							});
						}
					});

					return acc;
				},
				[] as Array<{ name: string; count: number; percent: string }>,
			);

			const reachCountsLength = reachCounts.reduce((acc, cat) => acc + cat.count, 0);
			reachCounts = reachCounts
				.map((reach) => {
					let percent = Number(((reach.count / reachCountsLength) * 100).toFixed(2));
					return {
						...reach,
						percent: `${percent}%`,
					};
				})
				.sort((a, b) => b.count - a.count);

			reachCounts.push({ name: 'Summe', count: reachCountsLength, percent: '100%' });

			return reachCounts;
		},
	},
	methods: {
		openReport() {
			const request = this.searchFormRef.request;

			localStorage.setItem('werberatReportData', JSON.stringify({ request, casesTotalCount: this.casesTotalCount }));

			const berichtRoute = this.$router.resolve({ name: 'Bericht' });
			window.open(berichtRoute.href, '_blank');
		},
		async doSearch() {
			const formRef = this.searchFormRef.$refs.formRef as FormInstance;

			const isValid = await validateForm(formRef, this.formId);
			if (!isValid) return;

			this.casesTableLoading = true;
			this.filteredCases = [];
			// Wichtig: Zurücksetzen des Zählers vor jeder Suche
			this.loadedCasesCount = 0;

			let searchRequest = prepareSearchRequest(this.searchFormRef.request) as SearchCaseRequest;
			if (searchRequest.states) {
				// @ts-ignore
				searchRequest.states = [searchRequest.states];
			}

			// Fasse die Prüfungsstatus zusammen (wichtig für backend search)
			if (searchRequest.states?.includes(CaseState.ExaminationSmallSenate)) {
				searchRequest.states.push(CaseState.ExaminationSmallSenateRunning);
			}
			if (searchRequest.states?.includes(CaseState.ExaminationWerberat)) {
				searchRequest.states.push(CaseState.ExaminationWerberatRunning);
			}

			let currentPage = 0;
			let totalCount = 0;

			do {
				const searchResult = await searchCases(searchRequest, currentPage, undefined, undefined, true);

				if (searchResult.success) {
					if (currentPage === 0) {
						totalCount = searchResult.data.totalCount;
						this.casesTotalCount = totalCount;
					}

					let allCases = [...this.filteredCases, ...searchResult.data.items];
					this.filteredCases = allCases;
					this.caseComplaints = allCases.map((c) => c.complaints).flat();
					this.caseComplaintsTotalCount = this.caseComplaints.length;
					this.loadedCasesCount += searchResult.data.items.length;
					currentPage++;
				} else {
					console.error('Fehler beim Laden der Daten:', searchResult.error);
					break;
				}
			} while (this.loadedCasesCount < totalCount);

			this.casesTableLoading = false;
		},
	},
	mounted() {
		this.doSearch();
	},
});
</script>
